import { Point, Wall, FloorPlanElement, RadiatorElement, Room } from '../types';
export type { RadiatorElement };
import { distanceToLine, calculateRooms, isPointInsideRoom } from './floorPlanUtils';
import { findElementRoomWithConsolidation } from '../services/roomSyncService';
import { SNAP_THRESHOLD, WALL_THICKNESS, RADIATOR_WALL_OFFSET } from '../constants/floorPlanConstants';

const DEFAULT_RADIATOR_WIDTH = 160; // 2x size for better visibility
const DEFAULT_RADIATOR_HEIGHT = 20; // This is depth from wall (2x size)

function getClosestPointOnSegment(point: Point, start: Point, end: Point): Point {
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    if (dx === 0 && dy === 0) return start;
    const t = ((point.x - start.x) * dx + (point.y - start.y) * dy) / (dx * dx + dy * dy);
    const clampedT = Math.max(0, Math.min(1, t));
    return { x: start.x + clampedT * dx, y: start.y + clampedT * dy };
}

export function findWallForRadiator(point: Point, walls: (Wall & { tempId: string, stableId: string })[]): (Wall & { tempId: string, stableId: string }) | null {
    let clickedWall: (Wall & { tempId: string, stableId: string }) | null = null;
    let minDistance = Infinity;

    for (const wall of walls) {
        const distance = distanceToLine(point, wall.start, wall.end);
        if (distance < SNAP_THRESHOLD * 1.5) {
            if (distance < minDistance) {
                minDistance = distance;
                clickedWall = wall;
            }
        }
    }
    return clickedWall;
}

export function createRadiatorElement(
    point: Point,
    wall: Wall & { tempId:string, stableId: string },
    currentFloor: number,
    allWalls?: Wall[],
    roomCustomLabels?: Record<string, string>
): RadiatorElement | null {
    const wallLength = Math.hypot(wall.end.x - wall.start.x, wall.end.y - wall.start.y);
    if (wallLength < DEFAULT_RADIATOR_WIDTH) return null;

    const closestPointOnWall = getClosestPointOnSegment(point, wall.start, wall.end);
    const distFromStart = Math.hypot(closestPointOnWall.x - wall.start.x, closestPointOnWall.y - wall.start.y);

    let positionOnWall = distFromStart / wallLength;

    const halfRadiatorWidthOnWall = (DEFAULT_RADIATOR_WIDTH / 2) / wallLength;
    positionOnWall = Math.max(halfRadiatorWidthOnWall, Math.min(positionOnWall, 1 - halfRadiatorWidthOnWall));

    const radiatorCenterOnWall = {
        x: wall.start.x + (wall.end.x - wall.start.x) * positionOnWall,
        y: wall.start.y + (wall.end.y - wall.start.y) * positionOnWall,
    };

    const wallDx = wall.end.x - wall.start.x;
    const wallDy = wall.end.y - wall.start.y;
    
    const wallAngle = Math.atan2(wallDy, wallDx);
    const perpVector = { x: -Math.sin(wallAngle), y: Math.cos(wallAngle) };

    const crossProduct = wallDx * (point.y - wall.start.y) - wallDy * (point.x - wall.start.x);
    const side = crossProduct > 0 ? -1 : 1;

    const totalOffset = (WALL_THICKNESS / 2 + RADIATOR_WALL_OFFSET + DEFAULT_RADIATOR_HEIGHT / 2) * side;

    const radiatorCenter = {
        x: radiatorCenterOnWall.x + perpVector.x * totalOffset,
        y: radiatorCenterOnWall.y + perpVector.y * totalOffset,
    };

    // Check if radiator placement is on the interior side of the wall
    if (allWalls && roomCustomLabels !== undefined) {
        const rooms = calculateRooms(allWalls.filter(w => w.floor === currentFloor), roomCustomLabels, 'geometryUpdate');
        const isInsideAnyRoom = rooms.some(room => isPointInsideRoom(radiatorCenter, room.walls));
        
        if (!isInsideAnyRoom) {
            // Try the opposite side if the current side is not inside any room
            const oppositeSide = -side;
            const oppositeTotalOffset = (WALL_THICKNESS / 2 + RADIATOR_WALL_OFFSET + DEFAULT_RADIATOR_HEIGHT / 2) * oppositeSide;
            const oppositeRadiatorCenter = {
                x: radiatorCenterOnWall.x + perpVector.x * oppositeTotalOffset,
                y: radiatorCenterOnWall.y + perpVector.y * oppositeTotalOffset,
            };
            
            const isOppositeInsideAnyRoom = rooms.some(room => isPointInsideRoom(oppositeRadiatorCenter, room.walls));
            
            if (isOppositeInsideAnyRoom) {
                // Use the opposite side if it's inside a room
                const newRadiator: RadiatorElement = {
                    id: `radiator-preview-${Date.now()}`,
                    type: 'radiator',
                    wallId: wall.stableId,
                    positionOnWall,
                    width: DEFAULT_RADIATOR_WIDTH,
                    height: DEFAULT_RADIATOR_HEIGHT,
                    floor: currentFloor,
                    position: oppositeRadiatorCenter,
                    rotation: wallAngle,
                    radiatorType: 'with-trvs',
                    side: oppositeSide,
                };
                
                return newRadiator;
            } else {
                // Neither side is inside a room, don't allow placement

                return null;
            }
        }
    }

    const newRadiator: RadiatorElement = {
        id: `radiator-preview-${Date.now()}`,
        type: 'radiator',
        wallId: wall.stableId,
        positionOnWall,
        width: DEFAULT_RADIATOR_WIDTH,
        height: DEFAULT_RADIATOR_HEIGHT,
        floor: currentFloor,
        position: radiatorCenter,
        rotation: wallAngle,
        radiatorType: 'with-trvs',
        side: side,
    };
    
    return newRadiator;
}

function drawSingleRadiator(ctx: CanvasRenderingContext2D, radiator: RadiatorElement, viewOffset: Point, scale: number, isEstate: boolean = false) {
    const { position, rotation, radiatorType } = radiator;

    // Use position directly since canvas context is already transformed
    ctx.save();
    ctx.translate(position.x, position.y);
    ctx.rotate(rotation);

    let text: string;
    switch (radiatorType) {
        case 'with-trvs':
            // 🏢 ESTATE MODE: Show RAD instead of RADT for cleaner estate agency PDFs
            text = isEstate ? 'RAD' : 'RADT';
            break;
        case 'without-trvs':
            text = 'RAD';
            break;
        case 'panel-heater':
            text = 'PH';
            break;
        case 'fan-heater':
            text = 'FH';
            break;
        case 'electric-storage-heater':
            text = 'ESH';
            break;
        case 'high-heat-retention':
            text = 'HHR';
            break;
        default:
            text = 'RAD';
    }
    
    // Adjust font size based on scale, but with a minimum and maximum size
    const baseFontSize = 12; // smaller base to reduce pill footprint
    const scaledFontSize = Math.max(7 / scale, Math.min(16 / scale, baseFontSize / scale));

    // Prepare font before measuring and compute generous padding
    ctx.font = `bold ${scaledFontSize}px sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    // Slightly reduced padding to avoid overlapping walls
    const paddingX = Math.max(1 / scale, scaledFontSize * 0.25);
    const paddingY = Math.max(0.5 / scale, scaledFontSize * 0.2);
    const textMetricsWidth = ctx.measureText(text).width || (text.length * scaledFontSize * 0.6);
    const boxWidth = textMetricsWidth + paddingX * 2;
    const boxHeight = scaledFontSize + paddingY * 2;
    
    // Draw pill-rounded background using a rounded rect path
    const corner = Math.min(boxHeight / 2, boxWidth / 2);
    // 🏢 ESTATE MODE: Use white background for professional B&W look
    ctx.fillStyle = isEstate ? '#ffffff' : '#fde047'; // white for estate, yellow for normal
    ctx.beginPath();
    ctx.moveTo(-boxWidth/2 + corner, -boxHeight/2);
    ctx.lineTo(boxWidth/2 - corner, -boxHeight/2);
    ctx.quadraticCurveTo(boxWidth/2, -boxHeight/2, boxWidth/2, -boxHeight/2 + corner);
    ctx.lineTo(boxWidth/2, boxHeight/2 - corner);
    ctx.quadraticCurveTo(boxWidth/2, boxHeight/2, boxWidth/2 - corner, boxHeight/2);
    ctx.lineTo(-boxWidth/2 + corner, boxHeight/2);
    ctx.quadraticCurveTo(-boxWidth/2, boxHeight/2, -boxWidth/2, boxHeight/2 - corner);
    ctx.lineTo(-boxWidth/2, -boxHeight/2 + corner);
    ctx.quadraticCurveTo(-boxWidth/2, -boxHeight/2, -boxWidth/2 + corner, -boxHeight/2);
    ctx.closePath();
    ctx.fill();

    // Draw black border
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 1 / scale;
    ctx.beginPath();
    ctx.moveTo(-boxWidth/2 + corner, -boxHeight/2);
    ctx.lineTo(boxWidth/2 - corner, -boxHeight/2);
    ctx.quadraticCurveTo(boxWidth/2, -boxHeight/2, boxWidth/2, -boxHeight/2 + corner);
    ctx.lineTo(boxWidth/2, boxHeight/2 - corner);
    ctx.quadraticCurveTo(boxWidth/2, boxHeight/2, boxWidth/2 - corner, boxHeight/2);
    ctx.lineTo(-boxWidth/2 + corner, boxHeight/2);
    ctx.quadraticCurveTo(-boxWidth/2, boxHeight/2, -boxWidth/2, boxHeight/2 - corner);
    ctx.lineTo(-boxWidth/2, -boxHeight/2 + corner);
    ctx.quadraticCurveTo(-boxWidth/2, -boxHeight/2, -boxWidth/2 + corner, -boxHeight/2);
    ctx.closePath();
    ctx.stroke();

    // Draw black text
    ctx.fillStyle = '#000000'; // black text
    ctx.fillText(text, 0, 0);

    ctx.restore();
}

export function drawRadiators(
    ctx: CanvasRenderingContext2D,
    elements: FloorPlanElement[],
    currentFloor: number,
    scale: number,
    viewOffset: Point,
    isEstate: boolean = false
) {
    const radiators = elements.filter((e: any) => e.type === 'radiator' && e.floor === currentFloor) as RadiatorElement[];

    for (const radiator of radiators) {
        drawSingleRadiator(ctx, radiator, viewOffset, scale, isEstate);
    }
}

export function drawRadiatorPreview(
    ctx: CanvasRenderingContext2D,
    radiatorPreview: RadiatorElement | null,
    scale: number,
    viewOffset: Point,
    isEstate: boolean = false
) {
    if (!radiatorPreview) return;

    ctx.save();
    ctx.globalAlpha = 0.7;
    drawSingleRadiator(ctx, radiatorPreview, viewOffset, scale, isEstate);
    ctx.restore();
}

/**
 * Calculate the exact position where the radiator text is drawn
 * This is where the radiator label appears after user fills in the prompt
 */
export function getRadiatorTextPosition(radiator: RadiatorElement): Point {
    // The text is drawn at the radiator's position (center of the radiator)
    // This is exactly where the text appears after the user saves radiator dimensions
    return {
        x: radiator.position.x,
        y: radiator.position.y
    };
}

/**
 * Check if there's a clear line-of-sight between two points (no walls blocking)
 */
function checkLineOfSight(from: Point, to: Point, walls: Wall[]): boolean {
    for (const wall of walls) {
        if (lineIntersectsWall(from, to, wall)) {
            return false;
        }
    }
    return true;
}

/**
 * Check if a line segment intersects with a wall
 */
function lineIntersectsWall(lineStart: Point, lineEnd: Point, wall: Wall): boolean {
    const line1Start = lineStart;
    const line1End = lineEnd;
    const line2Start = wall.start;
    const line2End = wall.end;
    
    // Calculate direction vectors
    const d1x = line1End.x - line1Start.x;
    const d1y = line1End.y - line1Start.y;
    const d2x = line2End.x - line2Start.x;
    const d2y = line2End.y - line2Start.y;
    
    // Calculate denominator for intersection formula
    const denominator = d1x * d2y - d1y * d2x;
    
    // Lines are parallel if denominator is 0
    if (Math.abs(denominator) < 1e-10) {
        return false;
    }
    
    // Calculate parameters for intersection point
    const dx = line2Start.x - line1Start.x;
    const dy = line2Start.y - line1Start.y;
    
    const t1 = (dx * d2y - dy * d2x) / denominator;
    const t2 = (dx * d1y - dy * d1x) / denominator;
    
    // Check if intersection occurs within both line segments
    return t1 >= 0 && t1 <= 1 && t2 >= 0 && t2 <= 1;
}

/**
 * Find room by wall ID (supports split walls and parent-child relationships)
 * Enhanced to handle shared walls between adjacent rooms
 */
function findRoomByWallId(wallId: string, rooms: Room[], radiatorPos: Point): Room | null {
    const matchingRooms: Room[] = [];
    
    for (const room of rooms) {
        for (const wall of room.walls) {
            let hasMatch = false;
            
            // Direct wall ID match
            if ((wall as any).stableId === wallId) {
                hasMatch = true;
            }
            // Split wall parent match
            else if ((wall as any).parentWallId === wallId) {
                hasMatch = true;
            }
            // Split wall sibling match (walls with same parent)
            else if ((wall as any).parentWallId && wallId.includes((wall as any).parentWallId)) {
                hasMatch = true;
            }
            
            if (hasMatch && !matchingRooms.includes(room)) {
                matchingRooms.push(room);
                break; // Move to next room once we found a match
            }
        }
    }
    
    if (matchingRooms.length === 0) {
        return null;
    }
    
    if (matchingRooms.length === 1) {
        console.log(`🔍 WALL_MATCH: Direct wall ID match in room "${matchingRooms[0].name}"`);
        return matchingRooms[0];
    }
    
    // 🔧 FIX: Multiple rooms share this wall (adjacent rooms with shared wall)
    // Use point-in-polygon to determine which room the radiator is actually in
    console.log(`⚠️ SHARED_WALL: ${matchingRooms.length} rooms share wall ID "${wallId}": ${matchingRooms.map(r => r.name).join(', ')}`);
    
    for (const room of matchingRooms) {
        const isInside = isPointInsideRoom(radiatorPos, room.walls);
        if (isInside) {
            console.log(`🔍 WALL_MATCH: Radiator IS inside room "${room.name}" (verified via geometry)`);
            return room;
        }
    }
    
    // Fallback: If geometry test fails for all rooms, use closest wall
    console.log(`⚠️ GEOMETRY_FAILED: Using closest wall distance for shared wall scenario`);
    return findRoomWithClosestWall(radiatorPos, matchingRooms);
}

/**
 * Find room with the closest wall to the given point
 */
function findRoomWithClosestWall(point: Point, rooms: Room[]): Room {
    let closestRoom = rooms[0];
    let minDistance = Infinity;
    
    for (const room of rooms) {
        for (const wall of room.walls) {
            // Calculate distance to wall segment
            const wallDistance = Math.min(
                Math.hypot(point.x - wall.start.x, point.y - wall.start.y),
                Math.hypot(point.x - wall.end.x, point.y - wall.end.y)
            );
            
            if (wallDistance < minDistance) {
                minDistance = wallDistance;
                closestRoom = room;
            }
        }
    }
    
    console.log(`🔍 CLOSEST_WALL: Room "${closestRoom.name}" has closest wall at ${minDistance.toFixed(1)}px`);
    return closestRoom;
}

/**
 * Simplified radiator detection using the exact same proven method as electric & gas meters
 * Uses findElementRoomWithConsolidation which prioritizes point-in-polygon geometry
 */
export function findRoomContainingRadiatorText(
    radiator: RadiatorElement, 
    rooms: Room[]
): Room | null {
    const radiatorPos = getRadiatorTextPosition(radiator);
    
    console.log(`🔥 RADIATOR_DETECTION: Using proven consolidation method for radiator at (${radiatorPos.x.toFixed(1)}, ${radiatorPos.y.toFixed(1)})`);
    
    // 🎯 Use the exact same method as gas/electric meters - most reliable
    // This uses point-in-polygon geometry FIRST before any wall-based detection
    const assignedRoom = findElementRoomWithConsolidation(radiatorPos, rooms, 'radiator');
    
    if (assignedRoom) {
        console.log(`🎯 RADIATOR_SUCCESS: Assigned to room "${assignedRoom.name}" via consolidation`);
        return assignedRoom;
    }
    
    console.log(`❌ RADIATOR_FAILED: No room could be assigned for radiator at (${radiatorPos.x.toFixed(1)}, ${radiatorPos.y.toFixed(1)})`);
    return null;
}

/**
 * Find all rooms that could potentially claim this radiator
 */
function findPotentialRadiatorRooms(radiatorPos: Point, rooms: Room[]): Room[] {
    const potentialRooms: Array<{ room: Room; score: number }> = [];
    const proximityThreshold = 350; // Further increased to catch rooms with wall proximity
    
    console.log(`🔍 POTENTIAL_ROOMS: Checking ${rooms.length} rooms for radiator at (${radiatorPos.x.toFixed(1)}, ${radiatorPos.y.toFixed(1)})`);
    
    for (const room of rooms) {
        const bounds = calculateRoomBounds(room.walls);
        const center = calculateRoomCenter(bounds);
        
        // Distance from radiator to room center
        const distance = Math.hypot(radiatorPos.x - center.x, radiatorPos.y - center.y);
        
        // Check if radiator is within expanded bounds (with tolerance)
        const tolerance = 100; // Increased tolerance for wall proximity cases
        const withinExpandedBounds = 
            radiatorPos.x >= (bounds.minX - tolerance) && radiatorPos.x <= (bounds.maxX + tolerance) &&
            radiatorPos.y >= (bounds.minY - tolerance) && radiatorPos.y <= (bounds.maxY + tolerance);
        
        const withinStrictBounds = 
            radiatorPos.x >= bounds.minX && radiatorPos.x <= bounds.maxX &&
            radiatorPos.y >= bounds.minY && radiatorPos.y <= bounds.maxY;
        
        // Additional check: wall proximity (similar to consolidated detection)
        let minWallDistance = Infinity;
        for (const wall of room.walls) {
            const wallDistance = Math.min(
                Math.hypot(radiatorPos.x - wall.start.x, radiatorPos.y - wall.start.y),
                Math.hypot(radiatorPos.x - wall.end.x, radiatorPos.y - wall.end.y)
            );
            minWallDistance = Math.min(minWallDistance, wallDistance);
        }
        
        console.log(`🔍 ROOM_CHECK: "${room.name}" - distance:${distance.toFixed(1)}px, minWall:${minWallDistance.toFixed(1)}px, bounds:(${bounds.minX.toFixed(1)},${bounds.minY.toFixed(1)}) to (${bounds.maxX.toFixed(1)},${bounds.maxY.toFixed(1)}), center:(${center.x.toFixed(1)},${center.y.toFixed(1)}), withinStrict:${withinStrictBounds}, withinExpanded:${withinExpandedBounds}`);
        
        // Accept room if either center distance OR wall proximity qualifies
        const qualifiesViaCenterDistance = distance <= proximityThreshold && withinExpandedBounds;
        const qualifiesViaWallProximity = minWallDistance <= 50 && withinExpandedBounds; // Wall proximity threshold
        
        if (qualifiesViaCenterDistance || qualifiesViaWallProximity) {
            // Score based on best qualifying metric
            const centerScore = qualifiesViaCenterDistance ? (proximityThreshold - distance) : 0;
            const wallScore = qualifiesViaWallProximity ? (100 - minWallDistance) : 0;
            const score = Math.max(centerScore, wallScore);
            
            potentialRooms.push({ room, score });
            const reason = qualifiesViaCenterDistance ? 'center-distance' : 'wall-proximity';
            console.log(`✅ POTENTIAL_CANDIDATE: "${room.name}" added (score: ${score.toFixed(1)}, reason: ${reason})`);
        } else {
            if (distance > proximityThreshold && minWallDistance > 50) {
                console.log(`❌ BOTH_REJECTED: "${room.name}" - center distance ${distance.toFixed(1)}px > ${proximityThreshold}px AND wall distance ${minWallDistance.toFixed(1)}px > 50px`);
            } else if (!withinExpandedBounds) {
                console.log(`❌ BOUNDS_REJECTED: "${room.name}" - outside expanded bounds`);
            }
        }
    }
    
    console.log(`🔍 POTENTIAL_SUMMARY: Found ${potentialRooms.length} potential rooms: ${potentialRooms.map(p => p.room.name).join(', ')}`);
    
    // Return rooms sorted by score (closest first)
    return potentialRooms
        .sort((a, b) => b.score - a.score)
        .map(p => p.room);
}

/**
 * Resolve conflicts when multiple rooms could claim the same radiator
 */
function resolveGenericRadiatorConflict(radiatorPos: Point, candidateRooms: Room[]): Room | null {
    if (candidateRooms.length <= 1) return candidateRooms[0] || null;
    
    console.log(`🔍 GENERIC_CONFLICT: Resolving radiator conflict between ${candidateRooms.map(r => r.name).join(' vs ')}`);
    
    const scoredRooms = candidateRooms.map(room => {
        const bounds = calculateRoomBounds(room.walls);
        const center = calculateRoomCenter(bounds);
        
        // 1. Distance to room center (closer = better)
        const distance = Math.hypot(radiatorPos.x - center.x, radiatorPos.y - center.y);
        const maxDimension = Math.max(bounds.maxX - bounds.minX, bounds.maxY - bounds.minY);
        const proximityScore = Math.max(0, 100 - (distance / maxDimension) * 100);
        
        // 2. Check if radiator is within room bounds (strict)
        const inBounds = radiatorPos.x >= bounds.minX && radiatorPos.x <= bounds.maxX &&
                        radiatorPos.y >= bounds.minY && radiatorPos.y <= bounds.maxY;
        const boundsScore = inBounds ? 100 : 0;
        
        // 3. Check geometric containment (point-in-polygon)
        const isInside = isPointInsideRoom(radiatorPos, room.walls);
        const geometryScore = isInside ? 200 : 0; // Highest priority
        
        // 4. Interior proximity (distance from walls)
        const interiorScore = calculateInteriorProximity(radiatorPos, room.walls);
        
        const totalScore = geometryScore + boundsScore + proximityScore + interiorScore;
        
        console.log(`🔍 SCORING: Room "${room.name}": geometry=${isInside}(${geometryScore}), bounds=${inBounds}(${boundsScore}), proximity=${proximityScore.toFixed(1)}, interior=${interiorScore.toFixed(1)} = ${totalScore.toFixed(1)}`);
        
        return { room, totalScore, distance };
    });
    
    // Return the highest scoring room
    const winner = scoredRooms.reduce((best, current) => 
        current.totalScore > best.totalScore ? current : best
    );
    
    console.log(`🎯 GENERIC_RESOLVED: Radiator assigned to "${winner.room.name}" (score: ${winner.totalScore.toFixed(1)}, distance: ${winner.distance.toFixed(1)}px)`);
    return winner.room;
}

/**
 * Find all rooms that could claim this radiator via wall matching
 */
function findAllWallBasedCandidates(radiator: RadiatorElement, rooms: Room[]): Array<{ room: Room; confidence: number; reason: string }> {
    if (!radiator.wallId) return [];
    
    const candidates: Array<{ room: Room; confidence: number; reason: string }> = [];
    
    for (const room of rooms) {
        for (const wall of room.walls) {
            let confidence = 0;
            let reason = '';
            
            // Direct wall ID match
            if ((wall as any).stableId === radiator.wallId) {
                confidence = 100;
                reason = 'exact_wall_match';
            }
            // Split wall parent-child relationship
            else if ((wall as any).parentWallId === radiator.wallId) {
                confidence = 90;
                reason = 'split_wall_child';
            }
            // Split wall sibling relationship
            else if ((wall as any).parentWallId && radiator.wallId.includes((wall as any).parentWallId)) {
                confidence = 85;
                reason = 'split_wall_sibling';
            }
            // Coordinate-based wall matching for split walls
            else if (isRadiatorOnWallSegment(radiator, wall)) {
                confidence = 75;
                reason = 'coordinate_wall_match';
            }
            
            if (confidence > 0) {
                // Check if this room is already a candidate
                const existingCandidate = candidates.find(c => c.room.name === room.name);
                if (!existingCandidate || confidence > existingCandidate.confidence) {
                    if (existingCandidate) {
                        // Update existing candidate with higher confidence
                        existingCandidate.confidence = confidence;
                        existingCandidate.reason = reason;
                    } else {
                        candidates.push({ room, confidence, reason });
                    }
                    console.log(`🔍 WALL_MATCH: Room "${room.name}" - confidence: ${confidence}, reason: ${reason}`);
                }
            }
        }
    }
    
    return candidates.sort((a, b) => b.confidence - a.confidence);
}

/**
 * Resolve conflicts when multiple rooms claim the same radiator via wall matching
 */
function resolveMultiRoomWallConflict(
    radiator: RadiatorElement, 
    wallCandidates: Array<{ room: Room; confidence: number; reason: string }>,
    allRooms: Room[]
): Room | null {
    const radiatorPos = radiator.position;
    
    console.log(`🤔 RESOLVING CONFLICT for radiator at (${radiatorPos.x.toFixed(1)}, ${radiatorPos.y.toFixed(1)})`);
    
    // Calculate enhanced scores for each candidate room
    const scoredCandidates = wallCandidates.map(candidate => {
        const room = candidate.room;
        const bounds = calculateRoomBounds(room.walls);
        const center = calculateRoomCenter(bounds);
        
        // 1. Distance to room center (closer = better)
        const centerDistance = Math.hypot(radiatorPos.x - center.x, radiatorPos.y - center.y);
        const maxDimension = Math.max(bounds.maxX - bounds.minX, bounds.maxY - bounds.minY);
        const proximityScore = Math.max(0, 100 - (centerDistance / maxDimension) * 100);
        
        // 2. Check if radiator is within room bounds
        const inBounds = radiatorPos.x >= bounds.minX && radiatorPos.x <= bounds.maxX &&
                        radiatorPos.y >= bounds.minY && radiatorPos.y <= bounds.maxY;
        const boundsScore = inBounds ? 50 : 0;
        
        // 3. Check geometric containment
        const isInside = isPointInsideRoom(radiatorPos, room.walls);
        const geometryScore = isInside ? 100 : 0;
        
        // 4. Wall confidence bonus
        const wallScore = candidate.confidence * 0.5; // Scale down wall confidence
        
        // 5. Check if radiator is closer to this room's interior
        const interiorScore = calculateInteriorProximity(radiatorPos, room.walls);
        
        const totalScore = geometryScore + boundsScore + proximityScore + wallScore + interiorScore;
        
        console.log(`🔍 CONFLICT_SCORING: Room "${room.name}":`);
        console.log(`   - geometry: ${isInside} (${geometryScore} pts)`);
        console.log(`   - bounds: ${inBounds} (${boundsScore} pts)`);
        console.log(`   - proximity: ${proximityScore.toFixed(1)} pts`);
        console.log(`   - wall: ${wallScore.toFixed(1)} pts`);
        console.log(`   - interior: ${interiorScore.toFixed(1)} pts`);
        console.log(`   - TOTAL: ${totalScore.toFixed(1)} pts`);
        
        return { ...candidate, totalScore };
    });
    
    // Return the highest scoring room
    const bestCandidate = scoredCandidates.reduce((best, current) => 
        current.totalScore > best.totalScore ? current : best
    );
    
    console.log(`🎯 CONFLICT_RESOLVED: Room "${bestCandidate.room.name}" selected (score: ${bestCandidate.totalScore.toFixed(1)})`);
    return bestCandidate.room;
}

/**
 * Calculate how close a point is to the interior of a room (vs edges/walls)
 */
function calculateInteriorProximity(point: Point, walls: Wall[]): number {
    let minDistanceToWall = Infinity;
    
    for (const wall of walls) {
        const wallLength = Math.hypot(wall.end.x - wall.start.x, wall.end.y - wall.start.y);
        if (wallLength === 0) continue;
        
        // Calculate closest point on wall to the point
        const wallVector = { x: wall.end.x - wall.start.x, y: wall.end.y - wall.start.y };
        const pointVector = { x: point.x - wall.start.x, y: point.y - wall.start.y };
        
        const dotProduct = (pointVector.x * wallVector.x + pointVector.y * wallVector.y) / (wallLength * wallLength);
        const projectionRatio = Math.max(0, Math.min(1, dotProduct));
        
        const closestPoint = {
            x: wall.start.x + projectionRatio * wallVector.x,
            y: wall.start.y + projectionRatio * wallVector.y
        };
        
        const distance = Math.hypot(point.x - closestPoint.x, point.y - closestPoint.y);
        minDistanceToWall = Math.min(minDistanceToWall, distance);
    }
    
    // Interior proximity score: higher distance from walls = more interior = higher score
    return Math.min(50, minDistanceToWall); // Cap at 50 points
}

/**
 * Find radiator room using wall association and split wall handling
 */
function findRadiatorRoomByWallAssociation(radiator: RadiatorElement, rooms: Room[]): Room | null {
    if (!radiator.wallId) return null;
    
    const candidates: Array<{ room: Room; confidence: number; reason: string }> = [];
    
    for (const room of rooms) {
        for (const wall of room.walls) {
            let confidence = 0;
            let reason = '';
            
            // Direct wall ID match
            if ((wall as any).stableId === radiator.wallId) {
                confidence = 100;
                reason = 'exact_wall_match';
            }
            // Split wall parent-child relationship
            else if ((wall as any).parentWallId === radiator.wallId) {
                confidence = 90;
                reason = 'split_wall_child';
            }
            // Split wall sibling relationship
            else if ((wall as any).parentWallId && radiator.wallId.includes((wall as any).parentWallId)) {
                confidence = 85;
                reason = 'split_wall_sibling';
            }
            // Coordinate-based wall matching for split walls
            else if (isRadiatorOnWallSegment(radiator, wall)) {
                confidence = 75;
                reason = 'coordinate_wall_match';
            }
            
            if (confidence > 0) {
                candidates.push({ room, confidence, reason });
                console.log(`🔍 WALL_MATCH: Room "${room.name}" - confidence: ${confidence}, reason: ${reason}`);
            }
        }
    }
    
    if (candidates.length === 0) return null;
    
    // Return highest confidence match
    const bestCandidate = candidates.reduce((best, current) => 
        current.confidence > best.confidence ? current : best
    );
    
    console.log(`🎯 WALL_BEST: Room "${bestCandidate.room.name}" selected (confidence: ${bestCandidate.confidence}, reason: ${bestCandidate.reason})`);
    return bestCandidate.room;
}

/**
 * Check if radiator is positioned on a wall segment using coordinates
 */
function isRadiatorOnWallSegment(radiator: RadiatorElement, wall: Wall): boolean {
    const radiatorPos = radiator.position;
    const tolerance = 30; // 30px tolerance for wall proximity
    
    // Calculate closest point on wall to radiator
    const wallLength = Math.hypot(wall.end.x - wall.start.x, wall.end.y - wall.start.y);
    if (wallLength === 0) return false;
    
    const wallVector = { x: wall.end.x - wall.start.x, y: wall.end.y - wall.start.y };
    const radiatorVector = { x: radiatorPos.x - wall.start.x, y: radiatorPos.y - wall.start.y };
    
    const dotProduct = (radiatorVector.x * wallVector.x + radiatorVector.y * wallVector.y) / (wallLength * wallLength);
    const projectionRatio = Math.max(0, Math.min(1, dotProduct));
    
    const closestPoint = {
        x: wall.start.x + projectionRatio * wallVector.x,
        y: wall.start.y + projectionRatio * wallVector.y
    };
    
    const distance = Math.hypot(radiatorPos.x - closestPoint.x, radiatorPos.y - closestPoint.y);
    return distance <= tolerance;
}

/**
 * Find radiator room using tolerance-based detection for complex geometries
 */
function findRadiatorRoomByTolerance(textPosition: Point, rooms: Room[]): Room | null {
    const candidates: Array<{ room: Room; score: number }> = [];
    
    for (const room of rooms) {
        const bounds = calculateRoomBounds(room.walls);
        const center = calculateRoomCenter(bounds);
        
        // Check if position is within room bounds
        const inBounds = textPosition.x >= bounds.minX && textPosition.x <= bounds.maxX &&
                        textPosition.y >= bounds.minY && textPosition.y <= bounds.maxY;
        
        if (!inBounds) continue;
        
        // Calculate distance to room center
        const centerDistance = Math.hypot(
            textPosition.x - center.x,
            textPosition.y - center.y
        );
        
        // Calculate room dimensions
        const roomWidth = bounds.maxX - bounds.minX;
        const roomHeight = bounds.maxY - bounds.minY;
        const maxDimension = Math.max(roomWidth, roomHeight);
        
        // Tolerance based on room size and complexity
        const wallCount = room.walls.length;
        const complexityFactor = wallCount > 4 ? 1.2 : 1.0; // More tolerance for complex rooms
        const toleranceRadius = (maxDimension * 0.4) * complexityFactor;
        
        if (centerDistance <= toleranceRadius) {
            // Score based on proximity to center (closer = higher score)
            const proximityScore = Math.max(0, 100 - (centerDistance / toleranceRadius) * 100);
            
            // Bonus for being well within bounds (not on edges)
            const marginX = roomWidth * 0.15;
            const marginY = roomHeight * 0.15;
            const wellWithinBounds = textPosition.x >= (bounds.minX + marginX) && 
                                   textPosition.x <= (bounds.maxX - marginX) && 
                                   textPosition.y >= (bounds.minY + marginY) && 
                                   textPosition.y <= (bounds.maxY - marginY);
            const boundsBonus = wellWithinBounds ? 20 : 0;
            
            const totalScore = proximityScore + boundsBonus;
            candidates.push({ room, score: totalScore });
            
            console.log(`🔍 TOLERANCE: Room "${room.name}" - distance: ${centerDistance.toFixed(1)}, tolerance: ${toleranceRadius.toFixed(1)}, score: ${totalScore.toFixed(1)}`);
        }
    }
    
    if (candidates.length === 0) return null;
    
    // Return highest scoring room
    const bestCandidate = candidates.reduce((best, current) => 
        current.score > best.score ? current : best
    );
    
    console.log(`🎯 TOLERANCE_BEST: Room "${bestCandidate.room.name}" selected (score: ${bestCandidate.score.toFixed(1)})`);
    return bestCandidate.room;
}

/**
 * Find the closest room to a position as final fallback
 */
function findClosestRoomToPosition(position: Point, rooms: Room[]): Room | null {
    if (rooms.length === 0) return null;
    
    let closestRoom = rooms[0];
    let closestDistance = Infinity;
    
    for (const room of rooms) {
        const bounds = calculateRoomBounds(room.walls);
        const center = calculateRoomCenter(bounds);
        
        const distance = Math.hypot(position.x - center.x, position.y - center.y);
        
        if (distance < closestDistance) {
            closestDistance = distance;
            closestRoom = room;
        }
    }
    
    return closestRoom;
}

/**
 * Calculate room bounds from walls
 */
function calculateRoomBounds(walls: Wall[]) {
    return walls.reduce((bounds, wall) => {
        return {
            minX: Math.min(bounds.minX, wall.start.x, wall.end.x),
            maxX: Math.max(bounds.maxX, wall.start.x, wall.end.x),
            minY: Math.min(bounds.minY, wall.start.y, wall.end.y),
            maxY: Math.max(bounds.maxY, wall.start.y, wall.end.y)
        };
    }, { minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity });
}

/**
 * Calculate room center from bounds
 */
function calculateRoomCenter(bounds: { minX: number; maxX: number; minY: number; maxY: number }) {
    return {
        x: (bounds.minX + bounds.maxX) / 2,
        y: (bounds.minY + bounds.maxY) / 2
    };
} 